import classNames from 'classnames';
import { action, observable, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { X7PushableBlockSnapshot, X8BlockSnapshot, X8BlockType } from '../../../domain';
import { X9TeacherComponent } from '../../content/x9/X9TeacherComponent';
import { ClassroomTasks } from '../stores/classroom-tasks';
import { TeacherClassroomSceneStore } from './TeacherClassroomSceneStore';
import swal from 'sweetalert2';
import { ReactNodeGenerator } from 'lib/helpers';
import mitt, { Emitter } from 'mitt';


function blockHeaderClassName(blockType: X8BlockType) {
  switch (blockType) {
    case 'Visible':
      return 'card-header alert-primary';
    case 'Pushable':
      return 'card-header alert-success';
    case 'Hidden':
      return 'card-header alert-warning';
    default:
      return 'card-header';
  }
}

@observer
class RenderBlockSnapshot extends React.Component<{
  store: TeacherClassroomSceneStore,
  blockSnapshot: X8BlockSnapshot,
  isPreview: boolean
}> {
  readonly mitt: Emitter<any> = mitt();

  @action
  revealBlock = (blockId: number) => {
    const mitt = this.props.store.hubStore.keyValues.mitt;
    swal.fire({ allowEscapeKey: false, allowOutsideClick: false });
    swal.showLoading();
    this.props.store.hubStore.keyValues.revealBlockId(blockId);
    mitt.on("reveal-merge-completed", async (e) => {
      swal.close();
      mitt.off("reveal-merge-completed");
    });
  };

  @action
  addBlockIdHomework = async (blockId: number) => {
    const mitt = this.props.store.hubStore.keyValues.mitt;
    swal.fire({ allowEscapeKey: false, allowOutsideClick: false });
    swal.showLoading();
    this.props.store.hubStore.keyValues.addBlockIdHomework(blockId);
    mitt.on("homework-merge-completed", async (e) => {
      swal.close();
      mitt.off("homework-merge-completed");
    });
  };

  @action
  removeBlockIdHomework = (blockId: number) => {
    const mitt = this.props.store.hubStore.keyValues.mitt;
    swal.fire({ allowEscapeKey: false, allowOutsideClick: false });
    swal.showLoading();
    this.props.store.hubStore.keyValues.removeBlockIdHomework(blockId);
    mitt.on("homework-merge-completed", async (e) => {
      swal.close();
      mitt.off("homework-merge-completed");
    });
  };

  constructor(
    props: {
      store: TeacherClassroomSceneStore,
      blockSnapshot: X8BlockSnapshot,
      isPreview: boolean
    }
  ) {
    super(props);
    makeObservable(this);
  }

  render() {
    const store = this.props.store;
    const block = this.props.blockSnapshot;
    // Push button
    const isRevealable = store.isRevealable(block);
    const showPushButtonDisabled = isRevealable && store.isRevealed(block);
    const showPushButton = isRevealable && !store.isRevealed(block) && !this.props.isPreview;
    // Homework button
    const isHomework = block.BlockType === 'Visible' || (block.BlockType === 'Pushable' && store.isRevealed(block));
    const showAddHomeworkButton = isHomework && !store.isAddedToHomework(block) && !this.props.isPreview;
    const showRemoveHomeworkButton = isHomework && store.isAddedToHomework(block) && !this.props.isPreview;

    return (
      <div className="card">
        <div className={blockHeaderClassName(block.BlockType)}>
          <div className="row">
            <div className="col"> Block</div>
            <div className="col-auto">
              {showPushButtonDisabled &&
                <button type="button" className="btn btn-outline-success" disabled>Pushed</button>
              }
              {showPushButton &&
                <button type="button" className="btn btn-primary"
                  onClick={() => this.revealBlock(block.Id)}>
                  Push/reveal
                </button>
              }
              {showAddHomeworkButton &&
                <button type="button" className="btn btn-primary"
                  onClick={() => this.addBlockIdHomework(block.Id)}>
                  Add to homework
                </button>
              }
              {showRemoveHomeworkButton &&
                <button type="button" className="btn btn-primary"
                  onClick={() => this.removeBlockIdHomework(block.Id)}>
                  Remove from homework
                </button>
              }
            </div>
          </div>
        </div>

        <div className="card-body">
          {block.ContentSnapshots?.length === 0 &&
            <div className="row justify-content-center">
              <div className="col-auto">
                <div className="alert alert-info">empty block</div>
              </div>
            </div>
          }

          {block.ContentSnapshots?.map((content) =>
            <X9TeacherComponent key={content.Id}
              x9={content}
              store={store.hubStore.keyValues} />
          )}

        </div>
      </div>
    );
  }
}

@observer
class RenderBlockSnapshots extends React.Component<{
  store: TeacherClassroomSceneStore,
  blocks: readonly X8BlockSnapshot[],
  isPreview: boolean
}> {
  render() {
    const store = this.props.store;
    const blocks = this.props.blocks;

    if (blocks === null || blocks.length === 0)
      return (
        <div className="row justify-content-center">
          <div className="col-auto">
            <div className="alert alert-info">empty pushable block</div>
          </div>
        </div>);

    return blocks.map((block) =>
      <RenderBlockSnapshot key={block.Id}
        store={store}
        blockSnapshot={block}
        isPreview={this.props.isPreview} />
    );
  }
}

@observer
class RenderPushableBlockSnapshot extends React.Component<{
  store: TeacherClassroomSceneStore,
  pushableBlockSnapshot: X7PushableBlockSnapshot,
  isPreview: boolean,
  isHomework: boolean,
}> {
  @action
  setCurrentPushableBlock = (pushableBlockId: number) => {
    this.props.store.hubStore.tasks.add(ClassroomTasks.setCurrentPushableBlock(pushableBlockId));
  };

  constructor(
    props: {
      store: TeacherClassroomSceneStore,
      pushableBlockSnapshot: X7PushableBlockSnapshot,
      isPreview: boolean,
      isHomework: boolean
    }
  ) {
    super(props);
    makeObservable(this);
  }

  *renderer(): ReactNodeGenerator {
    const pb = this.props.pushableBlockSnapshot;
    const store = this.props.store;
    const classroomState = this.props.store.hubStore.state.classroomState!;

    yield (
      <div className="card">
        {(!this.props.isPreview && !this.props.isHomework) &&
          <div className="card-header">
            <div>Pushable Block: {pb.Name}</div>
            <div>
              {
                pb.Id !== store.hubStore.currentPushableBlockId &&
                <button type="button" className="btn btn-primary"
                  disabled={!store.hubStore.tasks.active}
                  onClick={() => this.setCurrentPushableBlock(pb.Id)}>
                  Start Lesson
                </button>
              }
              {
                pb.Id === classroomState.CurrentPushableBlockId &&
                <span> Started </span>
              }
            </div>
          </div>
        }

        <div className="card-body">
          <RenderBlockSnapshots store={store}
            blocks={pb.BlockSnapshots!}
            isPreview={this.props.isPreview} />
        </div>
      </div>);
  }

  render() {
    return Array.from(this.renderer());
  }
}

@observer
class RenderPushableBlockSnapshots extends React.Component<{
  store: TeacherClassroomSceneStore,
  pushableBlockSnapshots: readonly X7PushableBlockSnapshot[],
  isPreview: boolean,
  isHomework: boolean,
}> {
  render() {
    const store = this.props.store;
    const snapshots = this.props.pushableBlockSnapshots;
    if (snapshots === null || snapshots.length === 0)
      return (
        <div className="row justify-content-center">
          <div className="col-auto">
            <div className="alert alert-info">empty page</div>
          </div>
        </div>);

    return snapshots.map((pb) =>
      <RenderPushableBlockSnapshot key={pb.Id}
        store={store}
        pushableBlockSnapshot={pb}
        isPreview={this.props.isPreview}
        isHomework={this.props.isHomework} />);
  }
}

@observer
export class TeacherClassroomTabs extends React.Component<{
  store: TeacherClassroomSceneStore,
  isPreview: boolean,
  isHomework: boolean
}> {
  @observable
  activeTab: number = 0;

  @action
  setActiveTab = (activeTab: number) => {
    this.activeTab = activeTab;
  };


  constructor(
    props: {
      store: TeacherClassroomSceneStore,
      isPreview: boolean,
      isHomework: boolean,
    }
  ) {
    super(props);
    makeObservable(this);
  }


  render() {
    const store = this.props.store;
    const snapshot = store.snapshot!;
    const classroomState = store.hubStore.state.classroomState!;
    if (snapshot === null || classroomState === null)
      return null;

    let activeTab = this.activeTab;

    if (snapshot.PageSnapshots!.length === 0)
      return (
        <div className="row">
          <div className="col">
            <div className="alert alert-info">Lesson is empty</div>
          </div>
        </div>);

    return (
      <div className="row">
        <div className="col">
          <Nav tabs>
            {snapshot?.PageSnapshots?.map(
              (page, index) =>
                <NavItem key={index}>
                  <NavLink className={classNames({ active: activeTab === index })}
                    onClick={() => this.setActiveTab(index)}>
                    {page.Name}
                  </NavLink>
                </NavItem>
            )
            }
          </Nav>
          <TabContent activeTab={activeTab}>
            {snapshot?.PageSnapshots?.map(
              (page, index) =>
                <TabPane tabId={index} key={index}>
                  <RenderPushableBlockSnapshots
                    store={store}
                    pushableBlockSnapshots={page.PushableBlockSnapshots!}
                    isPreview={this.props.isPreview}
                    isHomework={this.props.isHomework} />
                </TabPane>
            )}
          </TabContent>
        </div>
      </div>
    );
  }
}
